import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadContractDetailsForContractIds,
    loadPreferredPronounDetails,
    loadServiceDetailsForContractId
} from '../../../store/directusService';
import {
    clearBusinessLogicError,
    clearValidationErrors,
    createParticipant,
    loadContractConstants,
    searchEligibilityGroups
} from '../../../store/participantService';
import { loadTeamsForService } from '../../../store/userService';
import { getObjFromId } from '../../../utils/directusFunctions';
import {
    ADDRESS_LINE_1_MAX_LENGTH,
    ADDRESS_LINE_2_MAX_LENGTH,
    ADDRESS_LINE_3_MAX_LENGTH,
    CITY_MAX_LENGTH,
    MAX_EMAIL,
    MAX_NAME,
    MAX_PHONE,
    MAX_POSTCODE,
    names
} from '../../../utils/formValidation/commonConstants';
import { CONTRACT_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { uuid } from '../../../utils/stringUtils';
import {
    ADVISER,
    hasRole,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import ScreenError from '../../ui/messageBanners/ScreenError';

import { validate } from './validateCreateParticipant';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './createParticipant.module.css';

const acceptedRoles = [
    ADVISER,
    LOCAL_ADMIN,
    MANAGER,
    PRAP,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
];
const _MESSAGE = 'New participant created';

const CreateParticipant = () => {
    const dispatch = useDispatch();

    const initialState = {
        id: '',
        firstName: '',
        middleName: '',
        lastName: '',
        preferredName: '',
        preferredPronounsId: '',
        address1: '',
        address2: '',
        address3: '',
        city: '',
        postcode: '',
        primaryPhoneNumber: '',
        emailAddress: '',
        uniqueIdentifier: '',
        repeatUniqueIdentifier: '',
        uniqueIdentifierType: null,
        eligibilityGroupId: '',
        referralDate: new Date().toISOString().slice(0, 10),
        contractId: '',
        serviceId: '',
        teamId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [resetKey, setResetKey] = useState(uuid);
    const [errors, setErrors] = useState({});
    const [disableFields, setDisableFields] = useState(false);
    const [selectedContractDetails, setSelectedContractDetails] = useState({});
    const [services, setServices] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const teamsForService = useSelector((state) => state.entities.userService.teamsForService);
    const preferredPronounDetails = useSelector(
        (state) => state.entities.directusService.preferredPronounDetails
    );
    const serviceDetailsForContractId = useSelector(
        (state) => state.entities.directusService.serviceDetailsForContractId
    );
    const contractDetails = useSelector((state) => state.entities.directusService.contractDetails);
    const eligibilityGroupsForContractId = useSelector(
        (state) => state.entities.participantService.eligibilityGroupSearch
    );
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);
    const validationErrors = useSelector(
        (state) => state.entities.participantService.validationErrors
    );
    const businessLogicError = useSelector(
        (state) => state.entities.participantService.businessLogicError
    );

    // USE EFFECTS
    useEffect(() => {
        if (!loggedInUser.contractIds?.length) return;
        dispatch(loadContractDetailsForContractIds(loggedInUser.contractIds));
        preferredPronounDetails?.length < 1 && dispatch(loadPreferredPronounDetails());
    }, [loggedInUser.contractIds]);

    useEffect(() => {
        setDisableFields(!hasRole(acceptedRoles, loggedInUser.roles));
        setIsButtonDisabled(!hasRole(acceptedRoles, loggedInUser.roles));
    }, [loggedInUser.roles]);

    useEffect(() => {
        if (newEntry.contractId) {
            const contract = contractDetails.find((el) => el.id === newEntry.contractId);
            setSelectedContractDetails(contract);
            onUpdate('uniqueIdentifierType', contract?.unique_identifier_type.name);
            dispatch(loadServiceDetailsForContractId(newEntry.contractId));
            dispatch(searchEligibilityGroups({ contractId: newEntry.contractId }));
            dispatch(loadContractConstants(newEntry.contractId));
        }
    }, [newEntry.contractId]);

    useEffect(() => {
        if (!loggedInUser.serviceIds?.length || !serviceDetailsForContractId?.length) {
            setServices([]);
        } else {
            setServices(
                serviceDetailsForContractId.filter((el) => loggedInUser.serviceIds.includes(el.id))
            );
        }
    }, [serviceDetailsForContractId]);

    useEffect(() => {
        if (!newEntry.contractId) return;
        if (
            teamsForService.length &&
            !newEntry.teamId &&
            teamsForService.some((el) => el.id === contractConstants.defaultTeamId)
        ) {
            onUpdate('teamId', contractConstants.defaultTeamId);
        }
    }, [teamsForService]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _MESSAGE) {
            clearParticipant();
            isButtonDisabled && setIsButtonDisabled(false);
        }
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
        if (businessLogicError && Object.keys(businessLogicError).length > 0) {
            setErrors(businessLogicError);
            dispatch(clearBusinessLogicError());
        }
    }, [successMessage, errorMessage, validationErrors, businessLogicError]);

    // HELPER FNS
    const clearParticipant = () => {
        setNewEntry(initialState);
        setErrors({});
        setResetKey(uuid);
    };

    const getUniqueIdString = (label) => {
        if (!newEntry.uniqueIdentifierType || newEntry.uniqueIdentifierType === 'TEMPORARY')
            return label;
        return `${names[newEntry.uniqueIdentifierType]} ${label}`;
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateContract = (chosenId) => {
        ['contractId', 'serviceId', 'teamId', 'eligibilityGroupId'].forEach((el) => clearError(el));
        setNewEntry((prev) => ({
            ...prev,
            contractId: chosenId,
            serviceId: '',
            teamId: '',
            eligibilityGroupId: ''
        }));
    };

    const onUpdateService = (chosenId) => {
        ['serviceId', 'teamId'].forEach((el) => clearError(el));
        chosenId && dispatch(loadTeamsForService({ serviceIds: [chosenId] }));
        setNewEntry((prev) => ({
            ...prev,
            serviceId: chosenId,
            teamId: ''
        }));
    };

    const onUseTemporaryUniqueIdChange = (option) => {
        ['uniqueIdentifier', 'repeatUniqueIdentifier'].forEach((el) => clearError(el));
        setNewEntry((prev) =>
            option
                ? {
                      ...prev,
                      uniqueIdentifierType: 'TEMPORARY',
                      uniqueIdentifier: 'TEMPORARY',
                      repeatUniqueIdentifier: 'TEMPORARY'
                  }
                : {
                      ...prev,
                      uniqueIdentifierType: selectedContractDetails?.unique_identifier_type.name,
                      uniqueIdentifier: initialState.uniqueIdentifier,
                      repeatUniqueIdentifier: initialState.repeatUniqueIdentifier
                  }
        );
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(newEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setIsButtonDisabled(true);
        dispatch(createParticipant(newEntry, _MESSAGE));
    };

    // RENDER
    if (contractDetails?.length < 1) return <LoadingSpinner content={CONTRACT_DETAILS_ERROR} />;

    return (
        <div className={local.wrapper}>
            <div key={resetKey} className={form.formWrapper}>
                <form
                    onSubmit={onSubmit}
                    className={form.form}
                    data-testid="create-participant-form">
                    <h1 className={app.mainHeading}>Create Participant</h1>
                    <h2 className={app.sectionHeading}>Contract Details</h2>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id={'contractId'}
                                label={'Contract'}
                                placeholder="Select Contract..."
                                disabled={disableFields}
                                mandatory={true}
                                menuItems={contractDetails}
                                selectedId={newEntry.contractId}
                                selected={getObjFromId(contractDetails, newEntry.contractId)}
                                error={errors.contractId}
                                onChange={(chosenId) => onUpdateContract(chosenId)}
                            />
                            <SingleSelect
                                id={'serviceId'}
                                label={'Service'}
                                placeholder="Select Service..."
                                disabled={disableFields || !newEntry.contractId}
                                mandatory={true}
                                menuItems={services}
                                selectedId={newEntry.serviceId}
                                selected={getObjFromId(services, newEntry.serviceId)}
                                error={errors.serviceId}
                                onChange={(chosenId) => onUpdateService(chosenId)}
                            />
                        </div>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id={'teamId'}
                                label={'Team'}
                                placeholder="Select Team..."
                                disabled={disableFields || !newEntry.serviceId}
                                mandatory={true}
                                menuItems={teamsForService}
                                selectedId={newEntry.teamId}
                                selected={getObjFromId(teamsForService, newEntry.teamId)}
                                error={errors.teamId}
                                onChange={(chosenId) => onUpdate('teamId', chosenId)}
                            />
                            <SingleSelect
                                id={'eligibilityGroupId'}
                                label={'Eligibility Group'}
                                placeholder="Select Eligibility Group..."
                                disabled={disableFields || !newEntry.contractId}
                                menuItems={eligibilityGroupsForContractId}
                                selectedId={newEntry.eligibilityGroupId}
                                selected={getObjFromId(
                                    eligibilityGroupsForContractId,
                                    newEntry.eligibilityGroupId
                                )}
                                onChange={(chosenId) => onUpdate('eligibilityGroupId', chosenId)}
                            />
                            <DateSelect
                                id="referralDate"
                                label="Referral Date"
                                disabled={disableFields}
                                mandatory={true}
                                value={newEntry.referralDate}
                                error={errors.referralDate}
                                max={new Date().toISOString().slice(0, 10)}
                                onDateChange={(date) => onUpdate('referralDate', date)}
                            />
                        </div>
                    </div>

                    <h2 className={app.sectionHeading}>General Details</h2>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <TextInputField
                                id={'firstName'}
                                label={'First Name'}
                                placeholder={'Enter first name'}
                                disabled={disableFields}
                                mandatory={true}
                                maxLength={MAX_NAME}
                                value={newEntry.firstName || ''}
                                error={errors.firstName}
                                onChange={(e) => onUpdate('firstName', e.target.value)}
                            />
                            <TextInputField
                                id={'middleName'}
                                label={'Middle Name'}
                                placeholder={'Enter middle name'}
                                disabled={disableFields}
                                maxLength={MAX_NAME}
                                value={newEntry.middleName || ''}
                                error={errors.middleName}
                                onChange={(e) => onUpdate('middleName', e.target.value)}
                            />
                            <TextInputField
                                id={'lastName'}
                                label={'Last Name'}
                                placeholder={'Enter last name'}
                                disabled={disableFields}
                                maxLength={MAX_NAME}
                                value={newEntry.lastName || ''}
                                error={errors.lastName}
                                onChange={(e) => onUpdate('lastName', e.target.value)}
                            />
                            <TextInputField
                                id={'preferredName'}
                                label={'Preferred Name'}
                                placeholder={'Enter preferred name'}
                                disabled={disableFields}
                                maxLength={MAX_NAME}
                                value={newEntry.preferredName || ''}
                                error={errors.preferredName}
                                onChange={(e) => onUpdate('preferredName', e.target.value)}
                            />
                            <SingleSelect
                                id={'preferredPronouns'}
                                label={'Preferred Pronouns'}
                                placeholder="Select preferred pronoun..."
                                disabled={disableFields}
                                menuItems={preferredPronounDetails}
                                selectedId={newEntry.preferredPronounsId}
                                selected={getObjFromId(
                                    preferredPronounDetails,
                                    newEntry.preferredPronounsId
                                )}
                                onChange={(chosenId) => onUpdate('preferredPronounsId', chosenId)}
                            />
                            {newEntry.contractId &&
                                contractConstants.temporaryUniqueIdAvailable && (
                                    <RadioButtons
                                        id="useTemporaryUniqueId"
                                        label="Use Temporary Unique ID"
                                        disabled={disableFields}
                                        value={newEntry.uniqueIdentifierType === 'TEMPORARY'}
                                        onChange={(option) => onUseTemporaryUniqueIdChange(option)}
                                    />
                                )}
                            <TextInputField
                                id={'uniqueId'}
                                label={getUniqueIdString('Unique ID')}
                                placeholder={'Enter unique identifier'}
                                disabled={
                                    disableFields || newEntry.uniqueIdentifierType === 'TEMPORARY'
                                }
                                mandatory={true}
                                value={newEntry.uniqueIdentifier || ''}
                                error={errors.uniqueIdentifier}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => onUpdate('uniqueIdentifier', e.target.value)}
                            />
                            <TextInputField
                                id={'repeatUniqueId'}
                                label={getUniqueIdString('Repeat Unique ID')}
                                placeholder={'Repeat unique identifier'}
                                disabled={
                                    disableFields || newEntry.uniqueIdentifierType === 'TEMPORARY'
                                }
                                mandatory={true}
                                value={newEntry.repeatUniqueIdentifier || ''}
                                error={errors.repeatUniqueIdentifier}
                                onPaste={(e) => e.preventDefault()}
                                onChange={(e) => onUpdate('repeatUniqueIdentifier', e.target.value)}
                            />
                        </div>
                        <div className={form.formColumn}>
                            <TextInputField
                                id={'address1'}
                                label={'Address Line 1'}
                                placeholder={'Enter address line 1'}
                                disabled={disableFields}
                                maxLength={ADDRESS_LINE_1_MAX_LENGTH}
                                value={newEntry.address1 || ''}
                                error={errors.address1}
                                onChange={(e) => onUpdate('address1', e.target.value)}
                            />
                            <TextInputField
                                id={'address2'}
                                label={'Address Line 2'}
                                placeholder={'Enter address line 2'}
                                disabled={disableFields}
                                maxLength={ADDRESS_LINE_2_MAX_LENGTH}
                                value={newEntry.address2 || ''}
                                error={errors.address2}
                                onChange={(e) => onUpdate('address2', e.target.value)}
                            />
                            <TextInputField
                                id={'address3'}
                                label={'Address Line 3'}
                                placeholder={'Enter address line 3'}
                                disabled={disableFields}
                                maxLength={ADDRESS_LINE_3_MAX_LENGTH}
                                value={newEntry.address3 || ''}
                                error={errors.address3}
                                onChange={(e) => onUpdate('address3', e.target.value)}
                            />
                            <TextInputField
                                id={'city'}
                                label={'City'}
                                placeholder={'Enter city'}
                                disabled={disableFields}
                                maxLength={CITY_MAX_LENGTH}
                                value={newEntry.city || ''}
                                error={errors.city}
                                onChange={(e) => onUpdate('city', e.target.value)}
                            />
                            <TextInputField
                                id={'postcode'}
                                label={'Postcode'}
                                placeholder={'Enter postcode'}
                                disabled={disableFields}
                                maxLength={MAX_POSTCODE}
                                value={newEntry.postcode || ''}
                                error={errors.postcode}
                                onChange={(e) => onUpdate('postcode', e.target.value.toUpperCase())}
                            />
                            <TextInputField
                                id="primaryPhoneNumber"
                                type="tel"
                                label="Phone Number"
                                placeholder={'Enter phone number'}
                                disabled={disableFields}
                                maxLength={MAX_PHONE}
                                value={newEntry.primaryPhoneNumber || ''}
                                error={errors.primaryPhoneNumber}
                                onChange={(e) => {
                                    onUpdate('primaryPhoneNumber', e.target.value);
                                }}
                            />
                            <TextInputField
                                id={'emailAddress'}
                                label={'Email Address'}
                                placeholder={'Enter email address'}
                                disabled={disableFields}
                                maxLength={MAX_EMAIL}
                                value={newEntry.emailAddress || ''}
                                error={errors.emailAddress}
                                onChange={(e) => onUpdate('emailAddress', e.target.value)}
                            />
                        </div>
                    </div>
                    <Button
                        id="createParticipant"
                        content="Create"
                        disabled={isButtonDisabled || disableFields}
                    />
                    {errors.businessLogicError?.error && (
                        <ScreenError error={errors.businessLogicError} clearError={clearError} />
                    )}
                </form>
            </div>
        </div>
    );
};

export default CreateParticipant;
